import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LoanValidationService } from '../../services/loan-validation';
import { TruthManagement, EvaluationCompletionEvent } from '../../services/truth-management';
import { AuthService } from '../../services/auth.service';
import { ClientCapabilityService } from '../../services/client-capability.service';
import { ModalService } from '../../services/modal.service';
import { NotificationService } from '../../services/notification.service';
import { LoanValidation as LoanValidationModel, ValidationRun, Document, AdminDocument, DocumentKey, EvaluationFile, EvaluationRunResponse, EvaluationStatus, ActiveEvaluation, DashboardResponse, DashboardDocumentModel, DashboardEvalFile, EvalSummaryResponse, EvalSummaryDocumentModel, GroundTruthStatus, GroundTruthStatusType, ActiveRunInfo } from '../../models/truth-management.model';
import { GROUND_TRUTH_STATUS_CONFIG, getStatusColor, STATUS_SORT_ORDER } from '../../config/ground-truth-status.config';
import { Subscription, forkJoin, interval, of, from, Observable, Subject } from 'rxjs';
import { filter, takeWhile, switchMap, map, catchError, mergeMap, toArray, take, takeUntil, skip } from 'rxjs/operators';
import { DocumentFilesService } from '../../services/document-files.service'; 
import { CanComponentDeactivate } from '../../guards/can-deactivate.guard';
import { PageHeaderComponent } from '../page-header/page-header.component';

@Component({
  selector: 'app-loan-validation',
  imports: [CommonModule, FormsModule, PageHeaderComponent],
  templateUrl: './loan-validation.html',
  styleUrls: ['./loan-validation.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoanValidation implements OnInit, OnDestroy, CanComponentDeactivate {
  loanValidations: LoanValidationModel[] = [];
  validationRuns: ValidationRun[] = [];
  selectedValidation: LoanValidationModel | null = null;
  documents: Document[] = [];
  adminDocuments: AdminDocument[] = [];
  documentSearchTerm: string = ''; // Search term for document model
  documentKeysMap: Map<string, DocumentKey[]> = new Map();
  evaluationFiles: EvaluationFile[] = [];
  evaluationFilesMap: Map<string, EvaluationFile[]> = new Map();
  documentAttributesMap: Map<string, DashboardEvalFile[]> = new Map(); // Store eval files from dashboard
  dashboardModelsMap: Map<string, EvalSummaryDocumentModel> = new Map(); // Store summary models for evaluation run checks
  loadingEvalFiles = false;
  loadingFromAPI = true; // Start with loading true to show loader immediately
  isAuthenticated = false;
  isLoadingUserRole: boolean = false;
  private navigationSubscription: Subscription | null = null;
 
  showRunsModal = false;
  validationInProgress = false;
  runningValidations = new Map<number, boolean>();
  startingEvaluations = new Map<string, string>(); // Track which document models are starting evaluation ('all' or 'part')
  
  // Active evaluation tracking - now subscribes to service
  activeEvaluations = new Map<string, ActiveEvaluation>(); // Key: document_model
  pollingSubscriptions = new Map<string, Subscription>(); // Key: evaluation_id
  currentElapsedTime: string = ''; // Cached timer value for active evaluation
  private evaluationStateSubscription: Subscription | null = null; // Subscribe to service state changes
  private timerSubscription: Subscription | null = null; // Subscribe to timer updates (independent)
  private completionSubscription: Subscription | null = null; // Subscribe to completion events
  private fileStatusChangedSubscription: Subscription | null = null; // Subscribe to file status changes (cascades to ground truth status)
  private readonly destroy$ = new Subject<void>(); // For unsubscribing from observables
  private readonly STORAGE_KEY = 'active_evaluations';
  private readonly POLLING_INTERVAL = 5000; // 5 seconds
  private readonly MAX_POLLING_ERRORS = 3; // Stop polling after 3 consecutive errors
  private pollingErrorCounts = new Map<string, number>(); // Track errors per evaluation
  // Track active test runs (to disable full evaluation button)
  activeTestRuns = new Map<string, ActiveRunInfo>();
  private isInitialLoad = true; // Track if this is the first load (to skip modals on page refresh)
  private recentlyCompletedRuns = new Set<string>(); // Track recently completed runs to prevent duplicates
  private evaluationsRunningOnLoad = new Set<string>(); // Track evaluations that were RUNNING when page loaded (to allow their popups)
  private currentClientId: string | null = null; // Track current client ID for API calls
  
  // Sorting properties
  sortColumn: 'documentCode' | 'accuracy' | 'runs' | 'lastRun' | 'fileCount' | 'attributeCount' | 'truthCoverage' | 'groundTruthStatus' | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  
  // Add Document Dialog
  showAddDocumentDialog = false;
  newDocumentNumber = '';
  newDocumentName = '';
  newDocumentDescription = '';
  selectedDocumentCode: string | null = null; // Selected document code for filtering

    // Check if there are any active evaluations running, Used by both beforeunload and canDeactivate guards 
  hasRunningEvaluations(): boolean {
    // Check if any evaluation is currently active
    return this.activeEvaluations.size > 0 || 
           this.startingEvaluations.size > 0 || 
           this.validationInProgress;
  }

  /**
   * Browser tab close/refresh prevention, Shows browser's native "Leave site?" confirmation dialog
   */
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    // Only show warning if there are active evaluations
    if (this.hasRunningEvaluations()) {
      // Standard way to trigger browser's confirmation dialog
      event.preventDefault();
      
      // For legacy browsers (Chrome <119)
      event.returnValue = '';
      return;
    }
  }

  /**
   * Angular Router navigation prevention (CanDeactivate Guard)
   * Handles navigation to other routes, logout, etc.
   * Shows custom modal with option to continue or cancel
   */
  canDeactivate(): Observable<boolean> {
    // No running evaluations â†’ allow navigation immediately
    if (!this.hasRunningEvaluations()) {
      return of(true);
    }

    // Create Subject to communicate modal result back to router
    const navigationDecision$ = new Subject<boolean>();

    // Count active evaluations
    const activeCount = this.activeEvaluations.size + 
                        this.startingEvaluations.size;

    // Show warning modal
    this.modalService.show({
      type: 'warning' as any,
      size: 'medium' as any,
      title: 'Evaluation in Progress',
      message: `You have ${activeCount} active evaluation(s) running.\n\nIf you leave this page, the evaluations will continue in the background, but you won't be able to see their progress.\n\nAre you sure you want to leave?`,
      confirmText: 'Yes, Leave',
      cancelText: 'Stay on Page',
      allowOutsideClick: false,
      allowEscapeKey: false,
      onConfirm: () => {
        navigationDecision$.next(true);
        navigationDecision$.complete();
      },
      onCancel: () => {
        navigationDecision$.next(false);
        navigationDecision$.complete();
      }
    });

    return navigationDecision$.asObservable();
  }
  
  // Filtered documents based on search
  get filteredDocuments(): Document[] {
    if (!this.documentSearchTerm.trim()) {
      return this.documents;
    }
    const searchLower = this.documentSearchTerm.toLowerCase();
    return this.documents.filter(doc => 
      doc.documentNumber?.toLowerCase().includes(searchLower) ||
      doc.documentName?.toLowerCase().includes(searchLower)
    );
  }
  
  // Filter table by selected document
  onDocumentFilterChange(): void {
    // The table already uses loanValidations which is filtered by the selectedDocumentCode
    // So we just need to ensure the data is current
    this.cdr.detectChanges();
  }
  
  // Get filtered validations based on selected document and search term
  get filteredValidations(): LoanValidationModel[] {
    let result = this.loanValidations;
    if (this.selectedDocumentCode) {
      result = result.filter(v => v.loanNumber === this.selectedDocumentCode);
    }
    if (this.documentSearchTerm?.trim()) {
      const term = this.documentSearchTerm.trim().toLowerCase();
      result = result.filter(v => v.loanNumber?.toLowerCase().includes(term));
    }
    return result;
  }

  constructor(
    private loanValidationService: LoanValidationService,
    private truthManagementService: TruthManagement,
    public authService: AuthService,
    public router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
    private documentFilesService: DocumentFilesService,
    private notificationService: NotificationService,
    private clientCapability: ClientCapabilityService,
  ) {}

  ngOnInit(): void {
    // Check if user is authenticated
    this.isAuthenticated = this.authService.hasValidToken();
    // With MSAL, tokens are managed internally
    const tokenInfo = this.authService.getTokenInfo();
    if (tokenInfo) {
    }
    
    if (!this.isAuthenticated) {
      // Don't load any data if not authenticated
      this.loadingFromAPI = false;
      this.cdr.detectChanges();
      return;
    }
    
    // Subscribe to service state changes - INDEPENDENT subscription for spinner state
    // Uses detectChanges() for immediate re-render with OnPush strategy
    this.evaluationStateSubscription = this.truthManagementService.activeEvaluations$
      .subscribe((activeEvals) => {
        activeEvals.forEach((activeEval, key) => {
        });
        this.activeEvaluations = new Map(activeEvals);
        
        // CRITICAL: Use detectChanges() for immediate re-render
        // markForCheck() only marks dirty but doesn't guarantee CD runs
        // With OnPush + async data, we need immediate CD trigger
        this.cdr.detectChanges();
      });
    
    // Separate subscription for timer updates (independent of state changes)
    // This ensures spinner updates don't depend on timer emissions
    this.timerSubscription = this.truthManagementService.timers$
      .subscribe((timers) => {
        if (this.selectedValidation) {
          const fullEval = this.activeEvaluations.get(`${this.selectedValidation.loanNumber}:all`);
          const testEval = this.activeEvaluations.get(`${this.selectedValidation.loanNumber}:part`);
          const activeEval = fullEval || testEval;
          if (activeEval) {
            this.currentElapsedTime = timers.get(activeEval.evaluation_id) || '';
          } else {
            this.currentElapsedTime = '';
          }
          this.cdr.detectChanges();
        }
      });
    
    // Subscribe to completion events for popup, spinner clearing, and dashboard refresh
    this.completionSubscription = this.truthManagementService.evaluationCompleted$.subscribe(
      (event) => this.handleEvaluationCompletion(event)
    );
    
    // Subscribe to file status changes - cascades to recalculate ground truth status in loan-validation
    // When truth values change in truth-management, file status updates trigger document model status recalculation
    this.fileStatusChangedSubscription = this.truthManagementService.fileStatusChanged$.subscribe(
      (event) => this.recalculateGroundTruthStatusForModel(event.documentModel)
    );

    // Subscribe to client initialization and changes - reload dashboard on both
    // CRITICAL: Use switchMap to cancel pending dashboard loads when client changes
    this.authService.currentClient$
      .pipe(
        filter(client => client !== null),
        switchMap((client: { client_id: string; client_name: string }) => {
          this.currentClientId = client.client_id; // Store for fallback calls
          this.loadingFromAPI = true;
          this.evaluationsRunningOnLoad.clear();
          // Force-refresh (bypass cache) so the red-dot/needs_eval_run indicator is always
          // accurate on landing here, regardless of whether an upload/GT edit/eval elsewhere
          // remembered to invalidate the shared eval summary cache.
          return this.truthManagementService.refreshEvalSummary(client.client_id);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response: EvalSummaryResponse) => {
          this.processDashboardResponse(response);
          this.loadingFromAPI = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.loadingFromAPI = false;
          this.cdr.detectChanges();
        }
      });
    
    // Note: Active evaluations are now tracked only in memory, fetched fresh from API on load
    
    // Subscribe to router navigation events to reload when navigating to this route
    this.navigationSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (event.urlAfterRedirects === '/dashboard' || event.url === '/dashboard') {
          // Show loader immediately to prevent blank flash
          this.loadingFromAPI = true;
          this.cdr.detectChanges();
          // Clear cached data
          this.loanValidations = [];
          setTimeout(() => {
            if (this.isAuthenticated) {
              // Force-refresh so this page never shows a stale needs_eval_run/red-dot state.
              this.loadDashboard(true);
            } else {
              this.loadingFromAPI = false;
              this.cdr.detectChanges();
            }
          }, 0);
        }
      });
  }

  ngOnDestroy(): void {
    // Clean up subscription
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
    
    // Clean up service subscription
    if (this.evaluationStateSubscription) {
      this.evaluationStateSubscription.unsubscribe();
    }
    
    // Clean up timer subscription
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    
    // Clean up completion subscription
    if (this.completionSubscription) {
      this.completionSubscription.unsubscribe();
    }

    // Clean up file status changed subscription
    if (this.fileStatusChangedSubscription) {
      this.fileStatusChangedSubscription.unsubscribe();
    }

    // Clean up all polling subscriptions (now in service but keep for safety)
    this.pollingSubscriptions.forEach((subscription, evaluationId) => {
     subscription.unsubscribe();
    });
    this.pollingSubscriptions.clear();
    
    // Clean up RxJS subscriptions with destroy$
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  /**
   * Handle evaluation completion event from service
   * Clears spinner flags and refreshes dashboard
   * Note: Toaster notifications are handled centrally by TruthManagement service
   */
  private handleEvaluationCompletion(event: EvaluationCompletionEvent): void {
    const wasRunningOnLoad = this.evaluationsRunningOnLoad.has(event.evaluationId);
    // Skip popup ONLY if this is initial load AND evaluation was NOT running on load
    // (i.e., evaluation was already COMPLETED when page loaded)
    if (this.isInitialLoad && !wasRunningOnLoad) {
      this.startingEvaluations.delete(event.documentModel);
      this.cdr.detectChanges();
      return;
    }
    
    // Cleanup tracking set (evaluation completed, no longer needs tracking)
    if (wasRunningOnLoad) {
      this.evaluationsRunningOnLoad.delete(event.evaluationId);
    }
    
    // Prevent duplicate handling
    if (this.recentlyCompletedRuns.has(event.evaluationId)) {
      return;
    }
    this.recentlyCompletedRuns.add(event.evaluationId);
    
    // Remove from recent set after 5 seconds
    setTimeout(() => {
      this.recentlyCompletedRuns.delete(event.evaluationId);
    }, 5000);
    
    // 1. Clear spinner flags for this document model
    this.startingEvaluations.delete(event.documentModel);
    
    // 2. Refresh dashboard to get updated evaluation list
    // Note: Toaster notification is handled by TruthManagement service (single source of truth)
    // Force summary refresh so latest last_run timestamp is fetched from API.
    this.loadDashboard(true);
    
    // Force change detection
    this.cdr.detectChanges();
  }
  
  /**
 * Load files for modal using shared service
 * Uses targeted API instead of full dashboard
 */
  /**
   * Format duration in milliseconds to human-readable string
   */
  formatDuration(durationMs: number): string {
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    const secs = seconds % 60;
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    } else if (minutes > 0) {
      return `${mins}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }

  /**
   * Get elapsed time for active evaluation - reads from cached timer value
   * Updated automatically by subscription in ngOnInit
   */
  getElapsedTime(documentModel: string): string {
    return this.currentElapsedTime;
  }

  /**
   * Debug method to check evaluation runs - now uses API
   * Call from browser console: (window as any).loanValidationComponent.debugCompletedEvals()
   */
  debugCompletedEvals(): void {
  }

  logout(): void {
    this.modalService.showLogoutConfirmation(() => {
      this.authService.logout();
      this.isAuthenticated = false;
    });
  }

  /**
   * Get current user role — delegates to AuthService (single source of truth)
   */



  loadAdminDocuments(): void {
    this.loadingFromAPI = true;
    
    // Require client ID for API call
    if (!this.currentClientId) {
      return;
    }
    
    this.truthManagementService.getAdminDocuments(this.currentClientId).subscribe({
      next: (response: any) => {
        // Handle the actual API response format: { document_models: string[], count: number } or { document_types: string[], count: number }
        let documentTypes: string[] = [];
        
        if (response && response.document_models && Array.isArray(response.document_models)) {
          documentTypes = response.document_models;
        } else if (response && response.document_types && Array.isArray(response.document_types)) {
          documentTypes = response.document_types;
        } else if (response && Array.isArray(response)) {
          // Fallback: if response is already an array of strings
          documentTypes = response;
        }
        
        if (documentTypes.length > 0) {
          // Map string array to AdminDocument format
          this.adminDocuments = documentTypes.map((docType, index) => ({
            id: index + 1,
            document_code: docType,
            document_name: this.formatDocumentName(docType),
            description: '',
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }));
          // Map to LoanValidation format for display
          this.loanValidations = this.adminDocuments.map(doc => ({
            id: doc.id || 0,
            loanNumber: doc.document_code,
            runs: 0,
            lastRun: null,
            accuracy: 0,
            documentCount: 1,
            documentCodes: [doc.document_code],
            fileCount: 0,
            attributeCount: 0
          }));
          // Skip loading document keys for all documents (causes too many API calls)
          // Keys will be loaded on-demand when a specific document is selected
          // Instead, load evaluation files directly
          // The sequence: loadAllEvaluationFiles -> loadAttributeCounts -> loadTruthCoverage
          this.loadAllEvaluationFiles();
          
          this.loadingFromAPI = false;
          this.cdr.detectChanges();
        } else {
          this.loadingFromAPI = false;
          this.loadLoanValidations();
        }
      },
      error: (err) => {
        this.loadingFromAPI = false;
        this.loadLoanValidations();
      }
    });
  }
  
  // Helper to format document code into readable name
  formatDocumentName(code: string): string {
    return code
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private parseSummaryLastRun(lastRunRaw: string | null): Date | null {
    if (!lastRunRaw) {
      return null;
    }

    const dateStr = lastRunRaw.endsWith('Z') ? lastRunRaw : `${lastRunRaw}Z`;
    const parsed = new Date(dateStr);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private normalizeSummaryAccuracy(rawAccuracy: number): number {
    const numeric = Number(rawAccuracy);
    if (!Number.isFinite(numeric)) {
      return 0;
    }

    // Support both 0-1 and 0-100 scales from backend safely.
    if (numeric >= 0 && numeric <= 1) {
      return numeric * 100;
    }

    return numeric;
  }



  private mapSummaryStatus(
    statusRaw: string,
    filesCount: number,
    attributesCount: number
  ): GroundTruthStatus {
    const normalized = (statusRaw || '').toUpperCase() as GroundTruthStatusType;

    if (normalized in GROUND_TRUTH_STATUS_CONFIG) {
      const status = normalized as GroundTruthStatusType;
      const config = GROUND_TRUTH_STATUS_CONFIG[status];
      return {
        status,
        color: config.color,
        message: config.messageTemplate,
        details: `${Number(filesCount) || 0} samples, ${Number(attributesCount) || 0} fields`
      };
    }

    return {
      status: 'NO_SAMPLE',
      color: getStatusColor('NO_SAMPLE'),
      message: statusRaw || 'Unknown status',
      details: `${Number(filesCount) || 0} samples, ${Number(attributesCount) || 0} fields`
    };
  }

  /**
   * Process dashboard API response and update component state
   * Centralized method to ensure consistent data mapping
   */
  private processDashboardResponse(response: EvalSummaryResponse): void {
    if (!response.document_models || response.document_models.length === 0) {
      this.loanValidations = [];
      this.adminDocuments = [];
      return;
    }

    // Clear existing maps
    this.evaluationFilesMap.clear();
    this.documentAttributesMap.clear();
    this.dashboardModelsMap.clear();

    // Map document models to loanValidations
    this.loanValidations = response.document_models.map((docModel: EvalSummaryDocumentModel, index: number) => {
      // Store in dashboardModelsMap for reference
      this.dashboardModelsMap.set(docModel.document_model, docModel);

      return {
        id: index + 1,
        loanNumber: docModel.document_model,
        runs: 0, // Will be populated by checkForActiveRunsAfterDashboard if needed
        lastRun: this.parseSummaryLastRun(docModel.last_run),
        accuracy: this.normalizeSummaryAccuracy(docModel.accuracy || 0),
        documentCount: 1,
        documentCodes: [docModel.document_model],
        fileCount: docModel.files_count || 0,
        attributeCount: docModel.attributes_count || 0,
        truthCoverage: `0/${docModel.files_count || 0}(0%)`, // Default, updated by loadTruthCoverage
        truthCoverageCount: 0,
        groundTruthStatus: this.mapSummaryStatus(docModel.status, docModel.files_count, docModel.attributes_count),
        eval_files: [] // Populated separately if needed
      } as LoanValidationModel;
    });

    // Also populate adminDocuments for compatibility
    this.adminDocuments = response.document_models.map((docModel, index) => ({
      id: index + 1,
      document_code: docModel.document_model,
      document_name: this.formatDocumentName(docModel.document_model),
      description: '',
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }));
    // Check for active runs
    this.checkForActiveRunsAfterDashboard();

    // Reapply sorting if previously set
    if (this.sortColumn) {
      this.applySorting();
    }
  }

  /**
   * Load evaluation summary data from consolidated API
   * GET /api/v1/eval/summary
   */
  loadDashboard(forceRefresh: boolean = false): void {
    this.loadingFromAPI = true;
    
    // Clear tracking set for evaluations running on load (fresh state for each dashboard load)
    this.evaluationsRunningOnLoad.clear();
    
    // Get current clientId from AuthService
    const currentClient = this.authService.getCurrentClient();
    const clientId = currentClient?.client_id;
    // Cache-first fetch so Home and Loan Validation can reuse the same summary payload.
    // Force-refresh is used only on completion paths where latest timestamps are required.
    const summaryRequest = forceRefresh
      ? this.truthManagementService.refreshEvalSummary(clientId)
      : this.truthManagementService.getEvalSummary(clientId);

    summaryRequest.subscribe({
      next: (response: EvalSummaryResponse) => {
        if (!response.document_models || response.document_models.length === 0) {
          this.loadingFromAPI = false;
          this.loanValidations = [];
          this.cdr.detectChanges();
          return;
        }
        
        // Clear existing maps
        this.evaluationFilesMap.clear();
        this.documentAttributesMap.clear();
        this.dashboardModelsMap.clear();
        
        // Process each summary model
        this.loanValidations = response.document_models.map((docModel: EvalSummaryDocumentModel, index: number) => {
          this.dashboardModelsMap.set(docModel.document_model, docModel);

          const lastRun = this.parseSummaryLastRun(docModel.last_run);
          const accuracy = this.normalizeSummaryAccuracy(docModel.accuracy);
          const groundTruthStatus = this.mapSummaryStatus(docModel.status, docModel.files_count, docModel.attributes_count);

          return {
            id: index + 1,
            loanNumber: docModel.document_model,
            runs: 0,
            lastRun,
            accuracy,
            documentCount: 1,
            documentCodes: [docModel.document_model],
            fileCount: Number(docModel.files_count) || 0,
            attributeCount: Number(docModel.attributes_count) || 0,
            truthCoverage: '0/0(0%)',
            truthCoverageCount: 0,
            groundTruthStatus
          } as LoanValidationModel;
        });
        
        // Also populate adminDocuments for compatibility
        this.adminDocuments = response.document_models.map((docModel, index) => ({
          id: index + 1,
          document_code: docModel.document_model,
          document_name: this.formatDocumentName(docModel.document_model),
          description: '',
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));
        // CRITICAL: Dashboard API does NOT return RUNNING evaluations in latest_run
        // We must check for active runs using the same API that run-validation uses
        this.checkForActiveRunsAfterDashboard();
        
        // Reapply sorting if a column was previously sorted
        // This ensures sort order is preserved after data reload
        if (this.sortColumn) {
          this.applySorting();
        }
        
        this.loadingFromAPI = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.loadingFromAPI = false;
        // Fallback to old method if dashboard API fails
        this.loadAdminDocuments();
      }
    });
  }

  /**
 * Check ALL document models for active runs after dashboard loads
 * NEW APPROACH: Use consolidated /active-runs API + individual /eval/status calls
 * Benefits: 
 * - 1 API call for filter (vs 20 calls checking each model)
 * - Only fetch details for models with active runs (0-3 typically)
 * - 85-95% reduction in API calls
 */
private checkForActiveRunsAfterDashboard(): void {
  const documentCodes = this.loanValidations.map(v => v.loanNumber);
  
  if (documentCodes.length === 0) {
    return;
  }
  // Step 1: Get list of models with active runs (1 API call)
  this.truthManagementService.getActiveRuns().subscribe({
    next: (response) => {
      const totalActive = (response.active_eval_runs?.length || 0) + (response.active_test_runs?.length || 0);
      // Step 2: Process full evaluation runs
      if (response.active_eval_runs && response.active_eval_runs.length > 0) {
        response.active_eval_runs.forEach(run => {
          // Skip if already tracking this evaluation.
          // Use the synchronous service state (getActiveEvaluations) rather than the
          // component's this.activeEvaluations, which is updated via an async
          // BehaviorSubject subscription and may lag behind setActiveEvaluation() calls
          // made earlier in the same loadDashboard() response handler.
          if (this.truthManagementService.getActiveEvaluations().has(`${run.document_model}:all`)) {
            return;
          }
          // Fetch detailed status (1 API call per active run)
          this.truthManagementService.getEvaluationStatus(run.evaluation_id).subscribe({
            next: (status) => {
              // Build ActiveEvaluation object with all needed data
              const activeEval: ActiveEvaluation = {
                evaluation_id: run.evaluation_id,
                job_id: run.evaluation_id,
                document_model: run.document_model,
                file_count: status.total_files || 0,
                started_at: status.started_at || new Date().toISOString(),
                status: {
                  evaluation_id: run.evaluation_id,
                  status: status.status,
                  total_files: status.total_files || 0,
                  completed_files: status.completed_files || 0,
                  failed_files: status.failed_files || 0,
                  running_files: status.running_files || 0,
                  pending_files: status.pending_files || 0,
                  started_at: status.started_at || new Date().toISOString(),
                  completed_at: status.completed_at
                },
                run_type: 'all'
              };
              
              // Store in service - triggers BehaviorSubject emission
              this.truthManagementService.setActiveEvaluation(run.document_model, activeEval);
              
              // Track for popup handling
              this.evaluationsRunningOnLoad.add(run.evaluation_id);
              
              // Start polling for updates
              this.truthManagementService.startStatusPolling(run.evaluation_id, run.document_model);
            },
            error: (err) => {
            }
          });
        });
      }
      
     // Step 3: Process test runs (to disable full evaluation button)
if (response.active_test_runs && response.active_test_runs.length > 0) {
  response.active_test_runs.forEach(run => {
    // Store in activeTestRuns for backwards compatibility
    this.activeTestRuns.set(run.document_model, run);
    
    // Skip if already tracking this test run (same async-vs-sync guard as for full evals).
    if (this.truthManagementService.getActiveEvaluations().has(`${run.document_model}:part`)) {
      return;
    }
    
    // ✅ NEW: Also fetch details and store in activeEvaluations
    this.truthManagementService.getEvaluationStatus(run.evaluation_id).subscribe({
      next: (status) => {
        // Build ActiveEvaluation object for test run
        const activeEval: ActiveEvaluation = {
          evaluation_id: run.evaluation_id,
          job_id: run.evaluation_id,
          document_model: run.document_model,
          file_count: status.total_files || 0,
          started_at: status.started_at || new Date().toISOString(),
          status: {
            evaluation_id: run.evaluation_id,
            status: status.status,
            total_files: status.total_files || 0,
            completed_files: status.completed_files || 0,
            failed_files: status.failed_files || 0,
            running_files: status.running_files || 0,
            pending_files: status.pending_files || 0,
            started_at: status.started_at || new Date().toISOString(),
            completed_at: status.completed_at
          },
          run_type: 'part' // âœ… Mark as test run
        };
        
        // Store in activeEvaluations (enables button disable logic)
        this.truthManagementService.setActiveEvaluation(run.document_model, activeEval);
        
        // Start polling for test run updates too
        this.truthManagementService.startStatusPolling(run.evaluation_id, run.document_model);
      },
      error: (err) => {
      }
    });
  });
  
  // Trigger change detection to update button states
  this.cdr.detectChanges();
}
 
      },
      error: (err) => {
      }
    }); 
  }

  loadDocumentKeys(): void {
    const codes = this.adminDocuments.map(d => d.document_code);
    // Only load keys for a subset to avoid overwhelming the API
    const limitedCodes = codes.slice(0, 5); // Only load first 5 for now
    this.truthManagementService.getAllDocumentKeys(limitedCodes).subscribe({
      next: (responses) => {
        responses.forEach((response, index) => {
          if (response && response.document_code) {
            const activeKeys = response.keys ? response.keys.filter(k => k.is_active !== false) : [];
            this.documentKeysMap.set(response.document_code, activeKeys);
          } else {
            // Debug: log when document_code is missing
          }
        });
      },
      error: (err) => {
      }
    });
  }

  /**
   * Load all evaluation files from the API
   */
  loadAllEvaluationFiles(): void {
    this.loadingEvalFiles = true;
    
    // Clear the map before loading new data
    this.evaluationFilesMap.clear();
    this.truthManagementService.getEvaluationFiles().subscribe({
      next: (files) => {
        this.evaluationFiles = files;
        
        // Log sample file to see what fields are available
        if (files.length > 0) {
        }
        
        // Group files by document model
        let groupedCount = 0;
        files.forEach((file, index) => {
          const docModel = file.document_model || 'unknown';
          if (index < 3) {
          }
          if (!this.evaluationFilesMap.has(docModel)) {
            this.evaluationFilesMap.set(docModel, []);
          }
          this.evaluationFilesMap.get(docModel)!.push(file);
          groupedCount++;
        });
        // Update file counts in loanValidations
        this.updateFileCountsFromEvaluationFiles();
        
        // Now load attribute counts, which will then trigger truth coverage calculation
        this.loadAttributeCounts();
        
        this.loadingEvalFiles = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.loadingEvalFiles = false;
      }
    });
  }

  /**
   * Update file counts in loanValidations based on evaluation files
   */
  updateFileCountsFromEvaluationFiles(): void {
    // Log sample to see exact matching
    if (this.evaluationFilesMap.size > 0) {
      const firstKey = Array.from(this.evaluationFilesMap.keys())[0];
    }
    
    let matchCount = 0;
    this.loanValidations.forEach(validation => {
      const docCode = validation.loanNumber; // Using loanNumber as document_code
      const files = this.evaluationFilesMap.get(docCode) || [];
      validation.fileCount = files.length;
      if (files.length > 0) {
        matchCount++;
      }
    });
    this.cdr.detectChanges();
  }

  loadLoanValidations(): void {
    // Require client ID for API call
    if (!this.currentClientId) {
      return;
    }
    
    this.truthManagementService.getAdminDocuments(this.currentClientId).subscribe({
      next: (response: any) => {
        // Handle the actual API response format: { document_models: string[], count: number }
        let documentTypes: string[] = [];
        
        if (response && response.document_models && Array.isArray(response.document_models)) {
          documentTypes = response.document_models;
        } else if (response && response.document_types && Array.isArray(response.document_types)) {
          documentTypes = response.document_types;
        } else if (response && Array.isArray(response)) {
          documentTypes = response;
        }
        
        if (documentTypes.length === 0) {
          this.loanValidations = [];
          this.cdr.detectChanges();
          return;
        }
        // Map to AdminDocument format
        const adminDocuments = documentTypes.map((docType, index) => ({
          id: index + 1,
          document_code: docType,
          document_name: this.formatDocumentName(docType),
          description: '',
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));
        
        // Map to LoanValidation format for display
        this.loanValidations = adminDocuments.map(doc => ({
          id: doc.id || 0,
          loanNumber: doc.document_code,
          runs: 0,
          lastRun: null,
          accuracy: 0,
          documentCount: 1,
          documentCodes: [doc.document_code],
          fileCount: 0,
          attributeCount: 0
        }));
        // Reapply sorting if a column was previously sorted
        if (this.sortColumn) {
          this.applySorting();
        }
        
        // Load evaluation files to update file counts
        // Note: updateRunsDataFromStorage is called after file counts are updated
        this.loadAllEvaluationFiles();
        
        // Load attribute counts for each document model
        this.loadAttributeCounts();
        
        // Mark initial load as complete after first successful load
        if (this.isInitialLoad) {
          this.isInitialLoad = false;
        }
        
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.loanValidations = [];
        this.cdr.detectChanges();
      }
    });
  }



  loadDocumentCodes(validation: LoanValidationModel): void {
    this.loanValidationService.getDocumentCodesForLoan(validation.id).subscribe(
      codes => {
        validation.documentCodes = codes;
        validation.documentCount = codes.length;
      },
      error => {
        validation.documentCodes = [];
        validation.documentCount = 0;
      }
    );
  }

  /**
   * Run new validation for a specific loan validation
   * Shows confirmation modal before proceeding
   */
  /**
   * Run full validation for entire document model (no file selection)
   */
  runNewValidation(validation: LoanValidationModel): void {
    // Validate inputs first - critical check
    if (!validation || !validation.loanNumber) {
      this.notificationService.showError('Invalid validation data. Please try again.');
      return;
    }
    
    const documentModel = validation.loanNumber;
    
    // Prevent double-clicks - check if already starting or running same type (full eval)
    if (this.startingEvaluations.get(documentModel) || this.hasActiveFullEvaluation(documentModel)) {
      return;
    }
    
    // Show confirmation for full run (no file selection)
    const message = `Are you sure you want to run validation for all the samples in this document model?`;
    const details = [
      { label: 'Document Model', value: documentModel },
      { label: 'Run Type', value: 'Full Document Model' }
    ];
    
    // Show confirmation modal
    this.modalService.showConfirmation(
      message,
      () => {
        // User confirmed - execute full validation (no file IDs = full run)
        this.executeValidation(validation, undefined);
      },
      {
        title: 'Confirm Evaluation Run',
        confirmText: 'Yes, Run Evaluation',
        cancelText: 'Cancel',
        details: details
      }
    );
  }
  
  /**
   * Open prompt tuning interface for the selected document model
   */
  openPromptTuning(validation: LoanValidationModel): void {
    if (!validation || !validation.loanNumber) {
      this.notificationService.showError('Invalid validation data. Please try again.');
      return;
    }
    
    // Navigate to prompt tuning page
    this.router.navigate(['/prompt-tuning', validation.loanNumber], {
      queryParams: { source: 'evaluation-prompt-tuning' },
      state: { documentModel: validation.loanNumber }
    });
  }
  
  /**
   * Execute the validation after file selection
   * @param validation The validation to execute
   * @param fileIds Optional array of file UUIDs for partial validation (undefined = full run)
   */
  private executeValidation(validation: LoanValidationModel, fileIds?: string[]): void {
    const documentModel = validation.loanNumber;
    const isPartialRun = fileIds && fileIds.length > 0;
    if (isPartialRun) {
    }
    
    // SET LOADING STATE: Spinner shows when API call starts
    this.startingEvaluations.set(documentModel, isPartialRun ? 'part' : 'all');
    this.validationInProgress = true;
    this.runningValidations.set(validation.id, true);
    
    // Mark initial load complete when user starts evaluation
    // Ensures completion popup shows for user-initiated runs
    if (this.isInitialLoad) {
      this.isInitialLoad = false;
    }
    
    this.cdr.detectChanges();
    
    // Auth check - reset loading state if auth fails
    if (!this.isAuthenticated) {
      this.startingEvaluations.delete(documentModel);
      this.validationInProgress = false;
      this.runningValidations.set(validation.id, false);
      this.cdr.detectChanges();
      this.notificationService.showError('Please login to run validation.');
      return;
    }
    
    // ============================================
    // Pre-Flight Check: Now handled inside runEvaluationSafe() via API call
    // ============================================
    // Call API using SAFE wrapper with request deduplication and pre-flight check
    this.truthManagementService.runEvaluationSafe(documentModel, fileIds).subscribe({
      next: (response: EvaluationRunResponse) => {
        // Check if API returned FAILED status (e.g., no evaluation files found)
        if (response.status === 'FAILED') {
          this.startingEvaluations.delete(documentModel);
          this.validationInProgress = false;
          this.runningValidations.set(validation.id, false);
          this.cdr.detectChanges();
          
          const runLabelErr = isPartialRun ? 'Run test failed to start' : 'Evaluation run failed to start';
          const failReason = response.message ? 'No files found for evaluation' : 'Technical error';
          this.notificationService.showError(
            runLabelErr,
            [
              { label: 'Document model', value: documentModel, originalValue: documentModel },
              { label: 'Reason', value: failReason, originalValue: failReason }
            ]
          );
          return;
        }

        if (response.skipped_files && response.skipped_files.length > 0) {
          response.skipped_files.forEach((file: string) => {
          });
        }
        
        // CRITICAL: Check if ANY files will be processed
        if (response.file_count === 0) {
          // Reset loading states
          this.startingEvaluations.delete(documentModel);
          this.validationInProgress = false;
          this.runningValidations.set(validation.id, false);
          this.cdr.detectChanges();
          
          // Show structured error notification (matches evaluation-runs.ts)
          const runLabelErr = isPartialRun ? 'Run Test Failed to start' : 'Evaluation Run Failed to start';
          this.notificationService.showError(
            runLabelErr,
            [
              { label: 'Document model', value: documentModel, originalValue: documentModel },
              { label: 'Reason', value: 'no ground truth' }
            ]
          );
          return;
        }

        // Show ONLY ONE notification based on whether files were skipped
        if (response.skipped_count && response.skipped_count > 0) {
          // Has skipped files â†’ show detailed notification with document model and skipped count
          const displayDocName = documentModel ? 
            documentModel.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 
            'Document';
          
          // Dynamic message based on run type
          const runTypeMessage = isPartialRun ? 'Test run started successfully' : 'Evaluation run started successfully';
          
          this.notificationService.showSuccess(
            runTypeMessage,
            [
              { label: 'Document model', value: documentModel, originalValue: documentModel },
              { label: 'Skipped files', value: response.skipped_count ?? 0 }
            ]
          );
        } else {
          // No skipped files â†’ show simple notification (no document model details)
          this.notificationService.showRunInitiated(documentModel, isPartialRun ? 'part' : 'all');
        }
        
        const evaluationId = response.evaluation_id || response.job_id;
        if (!evaluationId) {
          // Reset loading states
          this.startingEvaluations.delete(documentModel);
          this.validationInProgress = false;
          this.runningValidations.set(validation.id, false);
          this.cdr.detectChanges();
          const noIdReason = response.message ? 'No files found for evaluation' : 'Technical error';
          this.notificationService.showError(isPartialRun ? 'Run test failed to start' : 'Evaluation run failed to start', [
            { label: 'Document model', value: documentModel, originalValue: documentModel },
            { label: 'Reason', value: noIdReason, originalValue: noIdReason }
          ]);
          return;
        }
        
        // Create active evaluation object WITH RUN TYPE
        // started_at is left blank — the service will anchor the timer to NOW
        // when the first RUNNING poll delivers status.started_at.
        const activeEval: ActiveEvaluation = {
          evaluation_id: evaluationId,
          job_id: response.job_id || evaluationId,
          document_model: documentModel,
          file_count: response.file_count || 0,
          started_at: '',  // blank until first RUNNING poll
          status: null,
          run_type: isPartialRun ? 'part' : 'all'  // Track run type
        };
        
        // Store active evaluation in service (ensures navigation persistence)
        this.truthManagementService.setActiveEvaluation(documentModel, activeEval);

        // Update local Map immediately so hasActiveEvaluation() returns true (ensures loader persists)
        this.activeEvaluations.set(`${documentModel}:${isPartialRun ? 'part' : 'all'}`, activeEval);
        
        // Start status polling in service (centralized polling) - CRITICAL for cross-component spinner sync
        this.truthManagementService.startStatusPolling(evaluationId, documentModel);
        
        // Trigger UI update to show active evaluation card
        this.cdr.detectChanges();
        
        // DON'T clear loading flags here - they'll be cleared by service when polling returns COMPLETED/FAILED
      },
      error: (error) => {
        // Reset all loading states on error
        this.startingEvaluations.delete(documentModel);
        this.validationInProgress = false;
        this.runningValidations.set(validation.id, false);
        this.cdr.detectChanges();
        
        const runLabelErr = isPartialRun ? 'Run test failed to start' : 'Evaluation run failed to start';
        const errorReason = (error.error?.message || error.error?.detail) ? 'No files found for evaluation' : 'Technical error';
        this.notificationService.showError(runLabelErr, [
          { label: 'Document model', value: documentModel, originalValue: documentModel },
          { label: 'Reason', value: errorReason, originalValue: errorReason }
        ]);
      }
    });
  }

  viewLoanRuns(validation: LoanValidationModel): void {
    // Navigate to run validation details page with runs tab active
    // Use validation.id as loanId, and 'latest' as placeholder runId
    this.router.navigate(['/run-validation', validation.id, 'latest', validation.loanNumber]);
  }

  closeRunsModal(): void {
    this.showRunsModal = false;
    this.selectedValidation = null;
  }

  // Component-level polling removed - using centralized TruthManagement service polling only

  /**
   * Stop polling for a specific evaluation
   */
  stopStatusPolling(evaluationId: string): void {
    const subscription = this.pollingSubscriptions.get(evaluationId);
    if (subscription) {
      subscription.unsubscribe();
      this.pollingSubscriptions.delete(evaluationId);
    }
    
    // Clear error counter
    this.pollingErrorCounts.delete(evaluationId);
    
    // Also stop in service
    this.truthManagementService.stopStatusPolling(evaluationId);
  }

  /**
   * Load attribute counts for each document model from the API
   * Using batched requests to avoid overwhelming the API
   */
  loadAttributeCounts(): void {
    // Process in batches of 5 to avoid overwhelming the API
    const batchSize = 5;
    const batches: any[][] = [];
    
    for (let i = 0; i < this.loanValidations.length; i += batchSize) {
      batches.push(this.loanValidations.slice(i, i + batchSize));
    }
    // Process batches sequentially
    let currentBatch = 0;
    
    const processBatch = () => {
      if (currentBatch >= batches.length) {
        this.cdr.detectChanges();
        this.loadTruthCoverage();
        return;
      }
      
      const batch = batches[currentBatch];
      const batchRequests = batch.map(validation =>
        this.truthManagementService.getDocumentKeys(validation.loanNumber).pipe(
          map(response => {
            validation.attributeCount = response.keys ? response.keys.length : 0;
            return validation;
          }),
          catchError(error => {
            if (error.error) {
            }
            validation.attributeCount = 0;
            return of(validation);
          })
        )
      );
      
      forkJoin(batchRequests).subscribe({
        next: () => {
          currentBatch++;
          this.cdr.detectChanges();
          // Small delay between batches
          setTimeout(() => processBatch(), 100);
        },
        error: (error) => {
          currentBatch++;
          // Continue with next batch even if this one fails
          setTimeout(() => processBatch(), 100);
        }
      });
    };
    
    processBatch();
  }

  /**
   * Load truth coverage (attributes with values) for all document models
   * Uses file-based approach: checks actual truth values in evaluation files
   */
  loadTruthCoverage(): void {
    this.loanValidations.forEach(validation => {
      const documentModel = validation.loanNumber;
      if (!validation.attributeCount || validation.attributeCount === 0) {
        validation.truthCoverage = `0/0(0%)`;
        validation.truthCoverageCount = 0;
        this.cdr.detectChanges();
        return;
      }
      
      // Get all evaluation files for this document model
      const filesForModel = this.evaluationFilesMap.get(documentModel) || [];
      if (filesForModel.length === 0) {
        validation.truthCoverage = `0/${validation.attributeCount}(0%)`;
        validation.truthCoverageCount = 0;
        this.cdr.detectChanges();
        return;
      }
      
      // Filter files with valid IDs
      const validFiles = filesForModel.filter(file => !!file.uuid);
      if (validFiles.length === 0) {
        validation.truthCoverage = `0/${validation.attributeCount}(0%)`;
        validation.truthCoverageCount = 0;
        this.cdr.detectChanges();
        return;
      }
      
      // Get attributes from all files with concurrency control (max 5 at a time)
      from(validFiles).pipe(
        mergeMap(file => {
          const fileId = file.uuid || '';
          return this.truthManagementService.getTruthValuesByFileId(fileId).pipe(
            map(response => response.attributes) // Extract attributes from AttributesResponse
          );
        }, 5), // Max 5 concurrent requests to prevent ERR_INSUFFICIENT_RESOURCES
        toArray() // Collect all results into an array
      ).subscribe({
        next: (allFileAttributes) => {
          // Flatten all attributes and collect unique attribute names with values
          const uniqueAttributeNames = new Set<string>();
          let totalValuesChecked = 0;
          let valuesWithContent = 0;
          
          allFileAttributes.forEach((fileAttrs: any[]) => {
            fileAttrs.forEach((attr: any) => {
              totalValuesChecked++;
              
              // Check if attribute has a non-empty value
              const hasValue = attr.value !== null && 
                              attr.value !== undefined && 
                              attr.value !== '' && 
                              String(attr.value).trim() !== '';
              
              if (hasValue) {
                valuesWithContent++;
                // Get attribute name and normalize (remove _1, _2 suffixes)
                const attrName = attr.attributeName || attr.attribute_name || attr.key_name;
                if (attrName) {
                  const normalizedName = attrName.replace(/_\d+$/, '');
                  uniqueAttributeNames.add(normalizedName);
                }
              }
            });
          });
          // Get golden truth schema to match against
          this.truthManagementService.getDocumentKeys(documentModel).subscribe({
            next: (response: any) => {
              const goldenTruthAttrs = response.keys || [];
              // Extract and normalize golden truth attribute names
              const goldenTruthNames = new Set(
                goldenTruthAttrs.map((attr: any) => attr.key_name || attr.key_label || attr.field_name)
                  .filter((name: string) => name)
              );
              // Count how many golden truth attributes are covered
              const coveredAttributes = new Set<string>();
              uniqueAttributeNames.forEach(attrName => {
                if (goldenTruthNames.has(attrName)) {
                  coveredAttributes.add(attrName);
                }
              });
              
              const coveredCount = coveredAttributes.size;
              const totalCount = validation.attributeCount || 0;
              const percentage = totalCount > 0 ? Math.round((coveredCount / totalCount) * 100) : 0;
              
              validation.truthCoverage = `${coveredCount}/${totalCount}(${percentage}%)`;
              validation.truthCoverageCount = coveredCount;
              this.cdr.detectChanges();
            },
            error: (error) => {
              validation.truthCoverage = `0/${validation.attributeCount}(0%)`;
              validation.truthCoverageCount = 0;
              this.cdr.detectChanges();
            }
          });
        },
        error: (error) => {
          validation.truthCoverage = `0/${validation.attributeCount}(0%)`;
          validation.truthCoverageCount = 0;
          this.cdr.detectChanges();
        }
      });
    });
  }

  /**
   * Clear all active evaluations (useful for debugging)
   */
  clearAllEvaluations(): void {
    this.pollingSubscriptions.forEach((sub, id) => {
      sub.unsubscribe();
    });
    this.pollingSubscriptions.clear();
    this.activeEvaluations.clear();
    this.runningValidations.clear();
    this.cdr.detectChanges();
  }

  /**
   * Get evaluation status for a document model
   */
  getEvaluationStatusForModel(documentModel: string): EvaluationStatus | null {
    const activeEval = this.activeEvaluations.get(`${documentModel}:all`) || this.activeEvaluations.get(`${documentModel}:part`);
    return activeEval?.status || null;
  }

  truncateDocumentModelName(name: string | null | undefined, maxLength: number = 24): string {
    if (!name) {
      return '-';
    }
    return name.length > maxLength ? `${name.slice(0, maxLength - 1)}â€¦` : name;
  }

  getMatchedFieldsForModel(documentModel: string): number {
    return this.getEvaluationStatusForModel(documentModel)?.summary?.total_matched_fields ?? 0;
  }

  getUnmatchedFieldsForModel(documentModel: string): number {
    const status = this.getEvaluationStatusForModel(documentModel);
    const total = status?.summary?.total_fields ?? 0;
    const matched = status?.summary?.total_matched_fields ?? 0;
    return Math.max(total - matched, 0);
  }

  getMatchedPercentForModel(documentModel: string): number {
    const status = this.getEvaluationStatusForModel(documentModel);
    const total = status?.summary?.total_fields ?? 0;
    if (!total) {
      return 0;
    }
    return Math.round((this.getMatchedFieldsForModel(documentModel) / total) * 100);
  }

  getUnmatchedPercentForModel(documentModel: string): number {
    const status = this.getEvaluationStatusForModel(documentModel);
    const total = status?.summary?.total_fields ?? 0;
    if (!total) {
      return 0;
    }
    return Math.round((this.getUnmatchedFieldsForModel(documentModel) / total) * 100);
  }

  getFileStatusDashArrayForModel(documentModel: string, type: 'completed' | 'running' | 'failed' | 'pending'): string {
    const status = this.getEvaluationStatusForModel(documentModel);
    const total = status?.total_files || 0;
    if (!total) {
      return '0 100';
    }

    let count = 0;
    switch (type) {
      case 'completed':
        count = status?.completed_files || 0;
        break;
      case 'running':
        count = status?.running_files || 0;
        break;
      case 'failed':
        count = status?.failed_files || 0;
        break;
      case 'pending':
        count = status?.pending_files || 0;
        break;
    }

    return `${(count / total) * 100} 100`;
  }

  getFileStatusOffsetForModel(documentModel: string, type: 'running' | 'failed' | 'pending'): number {
    const status = this.getEvaluationStatusForModel(documentModel);
    const total = status?.total_files || 0;
    if (!total) {
      return 0;
    }

    const completed = ((status?.completed_files || 0) / total) * 100;
    const running = ((status?.running_files || 0) / total) * 100;
    const failed = ((status?.failed_files || 0) / total) * 100;

    switch (type) {
      case 'running':
        return completed;
      case 'failed':
        return completed + running;
      case 'pending':
        return completed + running + failed;
    }
  }

  /**
   * Check if a document model has an active evaluation
   */
  hasActiveEvaluation(documentModel: string): boolean {
    const activeEval = this.activeEvaluations.get(`${documentModel}:all`) || this.activeEvaluations.get(`${documentModel}:part`);
    if (!activeEval) return false;
    
    const status = activeEval.status?.status;
    return status === 'RUNNING' || status === 'PENDING' || !status;
  }

  /**
   * Eye button availability should not wait for async runs enrichment.
   * Use immediately available signals first (active evaluation or lastRun).
   */
  isViewRunsEnabled(validation: LoanValidationModel): boolean {
    return this.hasActiveEvaluation(validation.loanNumber) || validation.runs > 0 || !!validation.lastRun;
  }

  getViewRunsTooltip(validation: LoanValidationModel): string {
    return this.isViewRunsEnabled(validation) ? 'View Runs' : 'No runs available';
  }

  /**
   * Check if a document model has an active FULL evaluation
   */
  hasActiveFullEvaluation(documentModel: string): boolean {
    const activeEval = this.activeEvaluations.get(`${documentModel}:all`);
    if (!activeEval) return false;
    
    const status = activeEval.status?.status;
    const isActive = status === 'RUNNING' || status === 'PENDING' || !status;
    const isFullRun = activeEval.run_type === 'all';
    return isActive && isFullRun;
  }

  /**
   * Check if ANY evaluation is in progress for a document model
   */
  isAnyEvaluationInProgress(documentModel: string): boolean {
    const startingFull = this.startingEvaluations.get(documentModel);
    return !!startingFull || this.hasActiveEvaluation(documentModel);
  }

  /**
   * Check if evaluation run needs to be initiated (User Story 7680)
   * Source of truth is backend summary field: needs_eval_run
   */
  needsEvaluationRun(documentModel: string): boolean {
    const summaryModel = this.dashboardModelsMap.get(documentModel);
    return !!summaryModel?.needs_eval_run;
  }
  /**
   * Get progress percentage for a document model
   */
  getEvaluationProgress(documentModel: string): number {
    const status = this.getEvaluationStatusForModel(documentModel);
    if (!status || status.total_files === 0) return 0;
    return Math.round((status.completed_files / status.total_files) * 100);
  }

  /**
   * Get the active evaluation ID for a document model
   */
  getActiveEvaluationId(documentModel: string): string {
    const activeEval = this.activeEvaluations.get(`${documentModel}:all`) || this.activeEvaluations.get(`${documentModel}:part`);
    return activeEval?.evaluation_id || '';
  }

  /**
   * Manually refresh evaluation status for a document model
   */
  refreshEvaluationStatus(documentModel: string): void {
    const activeEval = this.activeEvaluations.get(`${documentModel}:all`) || this.activeEvaluations.get(`${documentModel}:part`);
    if (!activeEval) return;
    this.truthManagementService.getEvaluationStatus(activeEval.evaluation_id).subscribe({
      next: (status) => {
        activeEval.status = status;
        this.cdr.detectChanges();
        
        // If completed or failed, stop polling
        if (status.status === 'COMPLETED' || status.status === 'FAILED') {
          this.stopStatusPolling(documentModel);
        }
      },
      error: (err) => {
      }
    });
  }

  isValidationRunning(validationId: number): boolean {
    return this.runningValidations.get(validationId) || false;
  }

  /**
   *  Check if a full evaluation is starting for a document model
   * Resilient to component navigation - checks both local state and service state
   * @param documentModel The document model to check
   * @returns true if full evaluation is starting or in early stages
   */
  isStartingEvaluation(documentModel: string): boolean {
    // Check local state first (fastest - set during API call)
    if (this.startingEvaluations.get(documentModel)) {
      return true;
    }
    // Fallback: Check service state (survives navigation)
    // If there's an active eval with no status yet = still starting
    const activeEval = this.activeEvaluations.get(`${documentModel}:all`);
    if (activeEval && activeEval.run_type === 'all' && !activeEval.status) {
      return true;
    }
    return false;
  }

  /**
   * Get tooltip text for evaluation run button with correct priority
   * Priority: 1) Validation running (full/test), 2) Indicator, 3) Default
   * Ensures consistent tooltip for both full evaluation and test runs
   * Persists correctly after hard refresh via activeEvaluations state
   */
  getEvaluationRunTooltip(documentModel: string): string {
    // Priority 1: Check if starting a full evaluation
    const startingRunType = this.startingEvaluations.get(documentModel);
    if (startingRunType === 'all') {
      return 'Evaluation run in progress';
    }

    // Priority 2: Check if a full evaluation is actively running
    const activeFullEval = this.activeEvaluations.get(`${documentModel}:all`);
    if (activeFullEval) {
      const status = activeFullEval.status?.status;
      const isActive = status === 'RUNNING' || status === 'PENDING' || !status;
      if (isActive) {
        return 'Evaluation run in progress';
      }
    }

    // Priority 3: Check indicator (only when no full evaluation is running)
    if (this.needsEvaluationRun(documentModel)) {
      return 'Evaluation needed - new files uploaded or ground truth modified';
    }

    // Priority 4: Default tooltip
    return 'Run Full Validation';
  }
  
  updateDocumentCount(validation: LoanValidationModel): void {
    // Fetch document codes from backend for this loan
    this.loanValidationService.getDocumentCodesForLoan(validation.id).subscribe(
      codes => {
        validation.documentCodes = codes;
        validation.documentCount = codes.length;
      },
      error => {
      }
    );
  }

  getDocumentCodesDisplay(validation: LoanValidationModel): string {
    if (!validation.documentCodes || validation.documentCodes.length === 0) {
      return 'No documents';
    }
    return validation.documentCodes.join(', ');
  }

  viewRunDetails(run: ValidationRun): void {
    // Pass the document code (loanNumber) for API lookup
    const documentCode = this.selectedValidation?.loanNumber || '';
    this.router.navigate(['/run-validation', run.loanId, run.runId, documentCode]);
  }

  navigateToFacts(validation: any): void {
    // Extract document code from loanNumber (e.g., "1-4_family_rider" or "DTSAUSDU - AUS-DU")
    let documentCode = validation.loanNumber;
    
    // If the loanNumber contains " - ", extract the part after it
    if (documentCode && documentCode.includes(' - ')) {
      const parts = documentCode.split(' - ');
      documentCode = parts[0]; // Use the code part before the dash
    }
    
    // Pass document code as-is (API expects exact original name)
    documentCode = documentCode.trim();
    // Navigate to Facts/Truth Management page with document code
    this.router.navigate(['/truth-management'], { 
      queryParams: { documentCode: documentCode } 
    }).then(success => {
    }).catch(error => {
    });
  }

  navigateToAdminSettings(): void {
    this.router.navigate(['/admin-settings']);
  }

  openImportDialog(): void {
    // Ask user for document code
    const documentCode = prompt('Enter the Document Code to import data for:');
    if (!documentCode || documentCode.trim() === '') {
      alert('Document Code is required to import data.');
      return;
    }
    
    // Confirm import
    const confirmImport = confirm(
      `Import data for Document Code: ${documentCode.trim()}?\n\n` +
      `This will read from the Excel file in the repo and import only rows matching this document code.`
    );
    
    if (confirmImport) {
      this.importExcelData(documentCode.trim());
    }
  }

  importExcelData(documentCode: string): void {
    this.truthManagementService.importExcel(documentCode).subscribe({
      next: (response) => {
        if (response.success) {
          const stats = response.stats;
          const data = response.data || [];
          
          // Build data display string
          let dataDisplay = '';
          if (data.length > 0) {
            dataDisplay = '\n\nðŸ“Š Imported Data:\n';
            data.forEach((item: any, index: number) => {
              dataDisplay += `${index + 1}. File: ${item.fileName}\n   Key: ${item.key}\n   Fact: ${item.fact}\n\n`;
            });
          }
          
          alert(
            `âœ… Import Successful for Document Code: ${documentCode}!\n\n` +
            `ðŸ“ˆ Statistics:\n` +
            `Total rows: ${stats.total_rows}\n` +
            `Files created: ${stats.files_created}\n` +
            `Attributes created: ${stats.attributes_created}\n` +
            `Truth values created: ${stats.truth_values_created}` +
            dataDisplay +
            `${stats.errors.length > 0 ? '\n   Errors:\n' + stats.errors.join('\n') : ''}`
          );
          
          // Reload data to show newly imported documents
          this.loadLoanValidations();
        } else {
          alert(
            `   Import completed with errors:\n\n` +
            `${response.stats.errors.join('\n')}`
          );
        }
      },
      error: (error) => {
        alert(`âŒ Import failed: ${error.error?.detail || error.message || 'Unknown error'}`);
      }
    });
  }

  sortBy(column: 'documentCode' | 'accuracy' | 'runs' | 'lastRun' | 'fileCount' | 'attributeCount' | 'truthCoverage' | 'groundTruthStatus'): void {
    if (this.sortColumn === column) {
      // Toggle direction if clicking the same column
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, default to ascending
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applySorting();
    
    // Trigger change detection to update UI after sorting
    this.cdr.detectChanges();
  }

  applySorting(): void {
    if (!this.sortColumn) return;
    
    this.loanValidations.sort((a, b) => {
      let compareValue = 0;
      
      switch (this.sortColumn) {
        case 'documentCode':
          // Safely handle null/undefined values for string comparison
          const aCode = a.loanNumber || '';
          const bCode = b.loanNumber || '';
          compareValue = aCode.localeCompare(bCode);
          break;
        case 'accuracy':
          compareValue = a.accuracy - b.accuracy;
          break;
        case 'runs':
          compareValue = a.runs - b.runs;
          break;
        case 'lastRun':
          // Handle null/invalid dates properly
          const aTime = a.lastRun && a.lastRun instanceof Date && !isNaN(a.lastRun.getTime()) 
            ? a.lastRun.getTime() 
            : 0;
          const bTime = b.lastRun && b.lastRun instanceof Date && !isNaN(b.lastRun.getTime()) 
            ? b.lastRun.getTime() 
            : 0;
          compareValue = aTime - bTime;
          break;
        case 'fileCount':
          compareValue = (a.fileCount || 0) - (b.fileCount || 0);
          break;
        case 'attributeCount':
          compareValue = (a.attributeCount || 0) - (b.attributeCount || 0);
          break;
        case 'truthCoverage':
          // Sort by percentage first, then by completed count as tiebreaker
          const aCoverage = this.parseTruthCoverage(a.truthCoverage);
          const bCoverage = this.parseTruthCoverage(b.truthCoverage);
          
          // Primary sort: by percentage
          compareValue = aCoverage.percentage - bCoverage.percentage;
          
          // Secondary sort: if percentages are equal, sort by completed count
          if (compareValue === 0) {
            compareValue = aCoverage.completed - bCoverage.completed;
          }
          break;
        case 'groundTruthStatus':
          // Sort by status priority using configuration
          // Healthy < No Sample < New Field < Field Missing < New Sample < Field Update
          const aPriority = a.groundTruthStatus ? STATUS_SORT_ORDER[a.groundTruthStatus.status] : 999;
          const bPriority = b.groundTruthStatus ? STATUS_SORT_ORDER[b.groundTruthStatus.status] : 999;
          compareValue = aPriority - bPriority;
          break;
      }
      
      return this.sortDirection === 'asc' ? compareValue : -compareValue;
    });
  }

  /**
   * Parse truth coverage string into components
   * @param coverage String like "5/10(50%)" or "0/0(0%)"
   * @returns Object with completed count, total count, and percentage
   */
  private parseTruthCoverage(coverage: string | undefined): { completed: number; total: number; percentage: number } {
    if (!coverage) return { completed: 0, total: 0, percentage: 0 };
    
    // Extract from format "x/y(z%)"
    const match = coverage.match(/(\d+)\/(\d+)\((\d+(?:\.\d+)?)%\)/);
    if (match) {
      return {
        completed: parseInt(match[1]) || 0,
        total: parseInt(match[2]) || 0,
        percentage: parseFloat(match[3]) || 0
      };
    }
    
    return { completed: 0, total: 0, percentage: 0 };
  }

  /**
   * Extract percentage value from truth coverage string (legacy method for compatibility)
   * @param coverage String like "5/10(50%)" or "0/0(0%)"
   * @returns Percentage as number (e.g., 50)
   */
  private extractPercentageFromCoverage(coverage: string | undefined): number {
    if (!coverage) return 0;
    
    // Extract percentage from format "x/y(z%)"
    const match = coverage.match(/\((\d+(?:\.\d+)?)%\)/);
    if (match && match[1]) {
      return parseFloat(match[1]);
    }
    
    return 0;
  }

  /**
   * Calculate Ground Truth Status for a document model
   * Uses configuration-based priority system for easy maintenance
   * Implements status hierarchy: Healthy > No Sample > Field Missing (P1) > Field Update (P2)
   */
  private calculateGroundTruthStatus(docModel: DashboardDocumentModel): GroundTruthStatus {
    // RULE 1: No Sample (exclusive)
    if (!docModel.eval_files || docModel.eval_files.length === 0) {
      const config = GROUND_TRUTH_STATUS_CONFIG.NO_SAMPLE;
      return {
        status: 'NO_SAMPLE',
        color: config.color,
        message: config.messageTemplate,
        details: config.detailsTemplate,
        count: 0
      };
    }

    // Check for issues in priority order
    const issues: Array<{ priority: number; status: GroundTruthStatusType; count: number; message: string; details: string }> = [];

    // P1: Field Missing - any sample missing any required field
    const fieldMissingCount = this.checkFieldMissing(docModel);
    if (fieldMissingCount > 0) {
      const config = GROUND_TRUTH_STATUS_CONFIG.FIELD_MISSING;
      issues.push({
        priority: config.priority,
        status: 'FIELD_MISSING',
        count: fieldMissingCount,
        message: config.messageTemplate,
        details: `${fieldMissingCount} sample(s) have incomplete data`
      });
    }

    // P2: Field Update - invalid enum values
    const fieldUpdateCount = this.checkInvalidEnums(docModel);
    if (fieldUpdateCount > 0) {
      const config = GROUND_TRUTH_STATUS_CONFIG.FIELD_UPDATE;
      issues.push({
        priority: config.priority,
        status: 'FIELD_UPDATE',
        count: fieldUpdateCount,
        message: config.messageTemplate,
        details: `${fieldUpdateCount} sample(s) have values that need correction`
      });
    }

    // Return highest priority issue (lowest priority number)
    if (issues.length > 0) {
      issues.sort((a, b) => a.priority - b.priority);
      const topIssue = issues[0];
      return {
        status: topIssue.status,
        color: getStatusColor(topIssue.status),
        message: topIssue.message,
        details: topIssue.details,
        count: topIssue.count
      };
    }

    // RULE 2: Healthy (no issues found)
    const config = GROUND_TRUTH_STATUS_CONFIG.HEALTHY;
    return {
      status: 'HEALTHY',
      color: config.color,
      message: config.messageTemplate,
      details: `${docModel.eval_files.length} samples, ${docModel.key_count} fields`,
      count: docModel.eval_files.length
    };
  }

  /**
   * Get color for status type (DEPRECATED - use getStatusColor from config)
   * Kept for backward compatibility, delegates to config
   */
  private getStatusColor(status: GroundTruthStatusType): 'green' | 'grey' | 'red' | 'yellow' | 'lightblue' | 'orange' {
    return getStatusColor(status);
  }

  /**
   * P1: Check for samples with missing required fields
   */
  private checkFieldMissing(docModel: DashboardDocumentModel): number {
    if (!docModel.keys || docModel.keys.length === 0) return 0;
    
    const requiredFields = docModel.keys.map(k => k.field_name);
    let samplesWithMissingFields = 0;

    // Build map of validatable fields (enum/signature/checkbox) with allowed values
    const validatableFieldsMap = new Map<string, string[]>();
    docModel.keys.forEach(key => {
      if ((key.feature === 'enum' || key.feature === 'signature' || key.feature === 'checkbox') 
          && key.allowed_values && key.allowed_values.length > 0) {
        validatableFieldsMap.set(key.field_name, key.allowed_values.map(v => v.toLowerCase()));
      }
    });

    docModel.eval_files.forEach(file => {
      // Get filled field names from this file (normalize array suffixes)
      const filledFields = new Set(
        file.attributes
          .filter(attr => {
            const value = attr.value ? String(attr.value).trim() : '';
            if (!value || value === '') return false;
            return true;
          })
          .map(attr => attr.attribute.trim().replace(/_\d+$/, ''))
      );

      // Check if any required field is missing
      const hasMissingField = requiredFields.some(field => !filledFields.has(field));
      if (hasMissingField) {
        samplesWithMissingFields++;
      }
    });

    return samplesWithMissingFields;
  }

  /**
   * P2: Check for samples with invalid enum values
   */
  private checkInvalidEnums(docModel: DashboardDocumentModel): number {
    const enumFieldsMap = new Map<string, string[]>();

    // Build map of enum fields and their allowed values
    docModel.keys.forEach(key => {
      // With my fix: Check ALL validatable fields
    if ((key.feature === 'enum' || key.feature === 'signature' || key.feature === 'checkbox') 
    && key.allowed_values && key.allowed_values.length > 0) {
    enumFieldsMap.set(key.field_name, key.allowed_values.map(v => v.toLowerCase())); 
    }
    });

    // If no enum fields defined, no validation needed
    if (enumFieldsMap.size === 0) return 0;

    let samplesWithInvalidEnum = 0;

    docModel.eval_files.forEach(file => {
      let hasInvalidEnum = false;

      file.attributes.forEach(attr => {
        // Get base field name (remove _1, _2 array suffixes)
        const baseFieldName = attr.attribute.replace(/_\d+$/, '');
        const allowedValues = enumFieldsMap.get(baseFieldName);

        if (allowedValues && attr.value && String(attr.value).trim() !== '') {
          const value = String(attr.value).trim().toLowerCase();
          if (value === 'n/a') {
          return;  // Don't validate N/A values
          }
          if (!allowedValues.includes(value)) {
            hasInvalidEnum = true;
          }
        }
      });

      if (hasInvalidEnum) {
        samplesWithInvalidEnum++;
      }
    });

    return samplesWithInvalidEnum;
  }

  /**
   * Recalculate ground truth status for a document model
   * Called when file status changes cascade from truth-management component
   * Efficiently updates only the affected document without API calls
   */
  private recalculateGroundTruthStatusForModel(documentModel: string): void {
    this.truthManagementService.refreshEvalSummary().subscribe({
      next: (response: EvalSummaryResponse) => {
        const summaryModel = response.document_models?.find(m => m.document_model === documentModel);
        if (!summaryModel) {
          return;
        }

        this.dashboardModelsMap.set(summaryModel.document_model, summaryModel);

        const validationIndex = this.loanValidations.findIndex(v => v.loanNumber === documentModel);
        if (validationIndex !== -1) {
          this.loanValidations[validationIndex].groundTruthStatus = this.mapSummaryStatus(
            summaryModel.status,
            summaryModel.files_count,
            summaryModel.attributes_count
          );
          this.loanValidations[validationIndex].accuracy = this.normalizeSummaryAccuracy(summaryModel.accuracy);
          this.loanValidations[validationIndex].lastRun = this.parseSummaryLastRun(summaryModel.last_run);
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
      }
    });
  }

  getSortIcon(column: 'documentCode' | 'accuracy' | 'runs' | 'lastRun' | 'fileCount' | 'attributeCount' | 'truthCoverage' | 'groundTruthStatus'): SafeHtml {
    if (this.sortColumn !== column) {
      return this.sanitizer.bypassSecurityTrustHtml('<div style="display:flex;flex-direction:column;align-items:center;line-height:0.8;font-family:Arial,sans-serif"><span>&#9650;</span><span>&#9660;</span></div>');
    }
    return this.sanitizer.bypassSecurityTrustHtml(this.sortDirection === 'asc' ? '&#9650;' : '&#9660;');
  }

  openAddDocumentDialog(): void {
    this.newDocumentNumber = '';
    this.newDocumentName = '';
    this.newDocumentDescription = '';
    this.showAddDocumentDialog = true;
  }

  closeAddDocumentDialog(): void {
    this.showAddDocumentDialog = false;
    this.newDocumentNumber = '';
    this.newDocumentName = '';
    this.newDocumentDescription = '';
  }

  addDocument(): void {
    if (!this.newDocumentNumber || !this.newDocumentNumber.trim()) {
      alert('Please enter document code');
      return;
    }

    if (!this.newDocumentName || !this.newDocumentName.trim()) {
      alert('Please enter document name');
      return;
    }

    const loanId = 1; // Default to loan ID 1

    this.truthManagementService.addDocument(
      loanId, 
      this.newDocumentNumber, 
      this.newDocumentName,
      this.newDocumentDescription || undefined
    ).subscribe({
      next: (newDocument) => {
        alert(`Document "${newDocument.documentNumber}" added successfully!`);
        this.closeAddDocumentDialog();
        this.loadLoanValidations(); // Reload the data
      },
      error: (error) => {
        alert(`Failed to add document: ${error.error?.detail || error.message}`);
      }
    });
  }

  navigateToVersioning(): void {
    this.router.navigate(['/versioning']);
  }

  navigateToSequencing(): void {
    this.router.navigate(['/']);
  }

  navigateToDataExtraction(): void {
    this.router.navigate(['/data-extraction']);
  }

  navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  navigateToOperationsRequestAnalytics(): void {
    this.clientCapability.navigateToOpsHealth();
  }
}


